'use client';

import * as DS from '@aurorasolar/ds';
import { Formik } from 'formik';
import debounce from 'lodash.debounce';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { ChangeEvent, useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import * as Yup from 'yup';

import { useTracking } from '@/analytics';
import { GetStartedCta, Subscribe } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';

import { PostSummary } from '../actions';
import { CategoriesList, Category } from '../components';
import { perPage } from '../paging';

import { PostList, toQueryString } from './PostList';

const validationSchema = Yup.object({
  category: Yup.array().of(Yup.string().required()),
  search: Yup.string().trim().defined().default(''),
});

type FormSchema = Yup.InferType<typeof validationSchema>;

type Props = { categories: Category[]; categorySlug?: string; posts: PostSummary[] };

export const BlogOverview = ({ categories, categorySlug, posts }: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const [isNavigating, setIsNavigating] = useState(false);
  const { trackEvent } = useTracking();

  useEffect(() => {
    setIsNavigating(false);
  }, [pathname, searchParams]);

  const breakpoint = DS.useBreakpoint();
  const pageSize = breakpoint === 'sm' ? perPage.sm : perPage.md;

  const initialValues: FormSchema = useMemo(() => {
    return {
      category: categorySlug ? [categorySlug] : [],
      search: searchParams.get('search') ?? '',
    };
  }, [categorySlug, searchParams]);

  const handleSubmit = useCallback(
    async ({ search }: FormSchema) =>
      startTransition(() => {
        const newParams = toQueryString(searchParams, { page: 1, search });
        router.push(newParams || pathname, { scroll: false });
      }),
    [router, pathname, searchParams]
  );

  const searchDebounced = useMemo(() => debounce((search) => handleSubmit({ search }), 700), [handleSubmit]);

  const categoryOptions: DS.IDropdownOption[] = useMemo(() => {
    return [
      { label: 'All', value: '' },
      ...categories.map(({ name, slug }) => ({ label: name ?? '', value: slug ?? '' })),
    ];
  }, [categories]);

  const firstPost = posts[0];

  const handleNavigate = useCallback(
    (slug: string) => {
      setIsNavigating(true);
      trackEvent(TRACKING.BLOG_READ_MORE, { page: slug });
    },
    [trackEvent]
  );

  const firstPostSlug = firstPost?.slug ?? '';
  const firstPostImageSrc = firstPost?.featuredImage?.node?.sourceUrl
    ? new URL(firstPost.featuredImage.node.sourceUrl).pathname
    : '';

  return (
    <>
      <DS.Flex bg="uiBgAlternate">
        <DS.Flex flexDirection={{ _: 'column', md: 'row' }} maxW={DS.BorealisTheme.screens.xl} mx="auto" rowGap={0}>
          {isNavigating ? (
            <DS.SkeletonLoader mb={0} minH={{ _: 600, md: 700 }} w="100%" />
          ) : (
            <>
              <DS.Flex flex={1} minH={{ _: 300, md: 700 }} position="relative">
                {firstPost ? (
                  <Image
                    alt={firstPost.featuredImage?.node?.altText ?? ''}
                    fill
                    priority
                    sizes="(max-width: 768px) 100vw, 50vw"
                    src={firstPostImageSrc}
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                  />
                ) : null}
              </DS.Flex>
              {firstPostSlug ? (
                <DS.Flex flex={1} minH={300}>
                  <DS.Flex flexDirection="column" gap="4" h="100%" justifyContent="center" p="4">
                    <DS.Text as="h1" text="d3" whiteSpace="wrap">
                      {firstPost?.title}
                    </DS.Text>
                    <DS.Text text="body20" whiteSpace="wrap">
                      {firstPost?.blogFields?.subheader}
                    </DS.Text>
                    <Link
                      href={`/home-solar/blog/${firstPost?.categories?.nodes?.[0]?.slug}/${firstPostSlug}`}
                      onClick={() => handleNavigate(firstPostSlug)}
                    >
                      <DS.Button icon={DS.IconArrowRight} iconPosition="right" mt="4">
                        Read now
                      </DS.Button>
                    </Link>
                  </DS.Flex>
                </DS.Flex>
              ) : null}
            </>
          )}
        </DS.Flex>
      </DS.Flex>

      <DS.Flex maxW={DS.BorealisTheme.screens.xl} mx="auto">
        <DS.Flex px="4" py="8">
          <DS.Divider />
          <DS.Grid>
            <DS.GridItem justifyContent="flex-end" size={{ _: 'full', md: 'third' }}>
              {categorySlug ? (
                <DS.Flex flexDirection="row" gap={1}>
                  <DS.Text>Blog</DS.Text>
                  <span>/</span>
                  <DS.Button as={Link} color="uiLink" href="/home-solar/blog" variant="text">
                    All Posts
                  </DS.Button>
                  <span>/</span>
                  <DS.Text ellipse>{categories.find((category) => category.slug === categorySlug)?.name}</DS.Text>
                </DS.Flex>
              ) : null}
            </DS.GridItem>
            <DS.GridItem size={{ _: 'full', md: 'two-thirds' }}>
              <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                submitOnBlur
                validationSchema={validationSchema}
              >
                <DS.Form flexDirection="row" justifyContent="end" w="100%">
                  <DS.Field
                    component={DS.TextInput}
                    icon={DS.IconMagnifyingGlass}
                    inputProps={{ fontSize: '16px !important' }}
                    labelProps={{ flexGrow: 1 }}
                    name="search"
                    onChangeCapture={(evt: ChangeEvent<HTMLInputElement>) => searchDebounced(evt.target.value)}
                    placeholder="Search blog library"
                    size="md"
                  />

                  <DS.Field
                    component={DS.DropdownInput}
                    handleSelectionChange={([selected]: string[]) => {
                      startTransition(() => {
                        router.push(`/home-solar/blog/${selected}${toQueryString(searchParams, { page: 1 })}`, {
                          scroll: false,
                        });
                      });
                    }}
                    id="blog-category-filter"
                    name="category"
                    options={categoryOptions}
                    placeholder="Filter"
                    size="md"
                    triggerProps={{ fontSize: '16px' }}
                  />
                </DS.Form>
              </Formik>
            </DS.GridItem>
          </DS.Grid>
        </DS.Flex>

        <DS.Flex flexDirection="row" gap={8} px={4}>
          <DS.Flex
            bg="uiBg"
            display={{ _: 'none', md: 'flex' }}
            flexShrink={0}
            gap={6}
            h="100%"
            maxWidth={320}
            overflow="hidden"
            position="sticky"
            top={0}
            w="25%"
          >
            <DS.Divider />
            <CategoriesList categories={categories} />
            <DS.Divider />
            <DS.Flex>
              <DS.Text>Get updates in your inbox</DS.Text>
              <DS.Box py={6}>
                <Subscribe icon={DS.IconArrowRight} variant="primary" />
              </DS.Box>
              <DS.Text>Subscribe and stay up to speed on the latest at Aurora.</DS.Text>
            </DS.Flex>
          </DS.Flex>
          <DS.Flex data-testid="blog-overview-post-list" flexShrink={1}>
            <DS.Flex alignItems="center" flexDirection="column" gap="4" h="100%" justifyContent="center" w="100%">
              <PostList loading={isPending} onNavigate={handleNavigate} pageSize={pageSize} posts={posts} />
            </DS.Flex>
          </DS.Flex>
        </DS.Flex>
      </DS.Flex>
      <GetStartedCta eventName={TRACKING.BLOG_GET_QUOTE}>
        <Subscribe buttonText="Subscribe" variant="primary" />
      </GetStartedCta>
    </>
  );
};
